import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  ArchivalUnitReference,
  NewOrder,
  Params,
  ResearchSheet,
  Result,
  UserInfo,
} from 'types';
import { SortOrder } from 'enums';

export interface TableHookProps<T> {
  fetchFunction: (params: Params) => Promise<Result<T>>;
  params?: Params;
}

export function useTableHook<T>({
  fetchFunction,
  params: defaultParams = {},
}: TableHookProps<T>) {
  const {
    field: defaultOrderBy = 'created',
    order: defaultOrder = SortOrder.DESC,
  } = defaultParams?.sort?.[0] ?? {};

  const [order, setOrder] = useState<SortOrder>(defaultOrder);
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [rows, setRows] = useState<T[]>([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const params: Params = {
      ...defaultParams,
      sort: [{ field: orderBy, order }],
      offset: page * rowsPerPage,
      size: rowsPerPage,
    };

    const getTableItems = async () => {
      try {
        const { items, count } = await fetchFunction(params);

        if (items && count !== undefined) {
          setCount(count);
          setRows(items);
        }
      } catch (e) {
        console.log(e);
      }
    };

    getTableItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderBy, page, rowsPerPage]);

  const returnValue = useMemo(
    () => ({
      order,
      setOrder,
      orderBy,
      setOrderBy,
      page,
      setPage,
      rowsPerPage,
      setRowsPerPage,
      rows,
      setRows,
      count,
      setCount,
    }),
    [count, order, orderBy, page, rows, rowsPerPage]
  );
  return returnValue;
}

export function useInitialValues(
  aisOrder?: NewOrder,
  archivalUnits?: ArchivalUnitReference[],
  userInfo?: UserInfo,
  userSheet?: ResearchSheet
): NewOrder {
  const [defaultId] = useState(uuidv4());
  return useMemo(() => {
    const {
      currentlyUsedSheetId,
      currentlyUsedSheetType,
      firstName,
      lastName,
      phone,
    } = userInfo ?? {};
    const { owner } = userSheet ?? {};
    const { firstName: userFirstName, lastName: userLastName } = owner ?? {};

    const fullName = [firstName, lastName].filter((s) => !!s).join(' ');
    const userFullName = [userFirstName, userLastName]
      .filter((s) => !!s)
      .join(' ');

    return aisOrder
      ? {
          id: defaultId,
          description: aisOrder.description,
          phone: aisOrder.phone,
          fullName: aisOrder.fullName,
          researchSheet: aisOrder.researchSheet,
          requestedForDate: aisOrder.requestedForDate,
          archivalAidMetadata: aisOrder.archivalAidMetadata,
          archivalCollection: aisOrder.archivalCollection,
          archivalUnits: aisOrder.archivalUnits,
          type: aisOrder.type,
        }
      : {
          id: defaultId,
          phone: !!userSheet ? owner?.phone : phone,
          fullName: !!userSheet ? userFullName : fullName,
          researchSheet: !!userSheet
            ? userSheet
            : {
                id: currentlyUsedSheetId!,
                type: currentlyUsedSheetType!,
              },
          archivalUnits,
        };
  }, [userInfo, userSheet, aisOrder, defaultId, archivalUnits]);
}
